<template>
	<div class="permission-matrix-page">
		<header class="header">
			<div class="header-top">
				<div>
					<h1>{{ $t("permissionMatrixPage.title") }}</h1>
					<p>{{ $t("permissionMatrixPage.subtitle") }}</p>
				</div>
			</div>
			<router-link to="/" class="back-link">{{ $t("permissionMatrixPage.backToHome") }}</router-link>
		</header>

		<div v-if="loading" class="loading">
			{{ $t("permissionMatrixPage.loading") }}
		</div>

		<div v-else-if="error" class="error">
			{{ error }}
		</div>

		<div v-else class="matrix-content">
			<div class="matrix-section-header">
				<h2>{{ $t("permissionMatrixPage.matrixTitle") }}</h2>
			</div>
			<div class="matrix-scroll">
				<table class="permission-table" aria-label="Permission matrix">
					<colgroup>
						<col class="permission-col" />
						<col class="description-col" />
						<col v-for="role in roles" :key="role.id" class="role-col" />
					</colgroup>
					<thead>
						<tr class="matrix-head-main">
							<th class="sticky-col permission-header" scope="col">{{ $t("permissionMatrixPage.permission") }}</th>
							<th class="sticky-col description-header" scope="col">
								{{ $t("permissionMatrixPage.descriptionLabel") }}
							</th>
							<th
								v-for="role in roles"
								:key="role.id"
								scope="col"
								class="role-header"
								:class="{ 'permission-set': PermissionSetRoleNames.includes(role.name) }"
							>
								{{ formatRoleName(role.name) }}
							</th>
						</tr>
						<tr class="matrix-head-description">
							<th class="sticky-col permission-summary" scope="col"></th>
							<th class="sticky-col description-summary" scope="col"></th>
							<th
								v-for="role in roles"
								:key="role.id"
								class="role-summary"
								:class="{ 'permission-set': PermissionSetRoleNames.includes(role.name) }"
								scope="col"
							>
								{{ role.description || $t("permissionMatrixPage.noDescription") }}
							</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="section in displayedSections" :key="section.title">
							<tr class="section-row">
								<th class="section-label" :colspan="2" scope="rowgroup">{{ section.title }}</th>
								<td :colspan="roles.length"></td>
							</tr>
							<tr v-for="row in section.rows" :key="`${section.title}-${row.label}`" class="permission-row">
								<th class="sticky-col permission-cell" scope="row">{{ row.label }}</th>
								<td class="sticky-col description-cell">{{ row.description }}</td>
								<td v-for="role in roles" :key="`${role.id}-${row.label}`" class="access-cell">
									<span v-if="cellFor(role, row)" class="access-badge" :class="`access-${cellFor(role, row)?.tone}`">
										{{ cellFor(role, row)?.label }}
									</span>
									<span v-else class="empty-access">—</span>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useRouter } from "vue-router"
import { Permission, UseRootStore } from "@/model/RootStore"
import { getRolesWithPermissions, type RoleWithPermissions } from "@/utils/ApiClient"
import { PermissionSetRoleNames } from "@/types/extendedAccess"

type AccessTone = "read" | "write" | "action"

type MatrixPermission = {
	name: string
	label: string
	tone: AccessTone
}

type MatrixRow = {
	label: string
	description: string
	permissions: MatrixPermission[]
}

type MatrixSection = {
	title: string
	rows: MatrixRow[]
}

const roleOrder = ["super_admin", "analyst", "broker", "coordinator", "prospector", "cca", "basic", "extended_access"]

const matrixSections: MatrixSection[] = [
	{
		title: "MAP LAYERS",
		rows: [
			{
				label: "Completed assessments",
				description: "Access the completed assessments map layer",
				permissions: [{ name: "completed-assessments.read", label: "Read", tone: "read" }],
			},
			{
				label: "Municipal meeting minutes",
				description: "Access the municipal meeting minutes point layer",
				permissions: [{ name: "city-pv-documents.read", label: "Read", tone: "read" }],
			},
			{
				label: "Agricultural zones",
				description: "Access the agricultural zones map layer",
				permissions: [{ name: "agriculturalzones.read", label: "Read", tone: "read" }],
			},
			{
				label: "Floodable zones",
				description: "Access the floodable zones map layer",
				permissions: [{ name: "floodablezones.read", label: "Read", tone: "read" }],
			},
			{
				label: "Land use",
				description: "Access the major land use map layers including RCM development plan and industrial land use",
				permissions: [{ name: "landuse.read", label: "Read", tone: "read" }],
			},
			{
				label: "Planning policy",
				description: "Access the planning policy map layer",
				permissions: [{ name: "planning-policy.read", label: "Read", tone: "read" }],
			},
			{
				label: "Prospects",
				description: "Access the prospects map layer",
				permissions: [{ name: "prospects.read", label: "Read", tone: "read" }],
			},
			{
				label: "Soil capability",
				description: "Access the soil capability map layer",
				permissions: [{ name: "soilcapability.read", label: "Read", tone: "read" }],
			},
			{
				label: "Transportation",
				description: "Access the transit lines and transit stations map layers",
				permissions: [{ name: "transportation.read", label: "Read", tone: "read" }],
			},
			{
				label: "Urban perimeters",
				description: "Access the urban perimeters map layer",
				permissions: [{ name: "urbanperimeters.read", label: "Read", tone: "read" }],
			},
			{
				label: "Urban priorities",
				description: "Access or update the urban priorities map layer",
				permissions: [
					{ name: "urbanpriorities.write", label: "Write", tone: "write" },
					{ name: "urbanpriorities.read", label: "Read", tone: "read" },
				],
			},
			{
				label: "Wetlands",
				description: "Access the wetlands map layer",
				permissions: [{ name: "wetlands.read", label: "Read", tone: "read" }],
			},
			{
				label: "Endangered species",
				description: "Access the endangered species map layer",
				permissions: [{ name: "endangered-species.read", label: "Read", tone: "read" }],
			},
		],
	},
	{
		title: "OPPORTUNITIES & TARGETS",
		rows: [
			{
				label: "Opportunity",
				description:
					"Read all opportunity types including listing_potential, off_market_official, and internal marketing-type opportunities",
				permissions: [{ name: "opportunity.read", label: "Read", tone: "read" }],
			},
			{
				label: "Opportunity-offmarket (clear)",
				description: "View unobfuscated off-market opportunity data",
				permissions: [{ name: "opportunity-offmarket.clear", label: "Clear", tone: "action" }],
			},
			{
				label: "Opportunity-target",
				description: "Create and manage opportunity targets",
				permissions: [{ name: "opportunity-target.write", label: "Write", tone: "write" }],
			},
			{
				label: "Opportunity-target (move)",
				description: "Move an opportunity target to another project",
				permissions: [{ name: "opportunity-target.move", label: "Move", tone: "action" }],
			},
			{
				label: "Opportunity-sensitive",
				description: "Read or update sensitive opportunity fields such as stage and priority",
				permissions: [
					{ name: "opportunity-sensitive.write", label: "Write", tone: "write" },
					{ name: "opportunity-sensitive.read", label: "Read", tone: "read" },
				],
			},
			{
				label: "Opportunity client notes",
				description: "Write client-facing notes on opportunity evaluations",
				permissions: [{ name: "opportunity-client-note.write", label: "Write", tone: "write" }],
			},
		],
	},
	{
		title: "TRANSACTIONS & PARCELS",
		rows: [
			{
				label: "Transaction",
				description: "Read, create, and update transaction data",
				permissions: [
					{ name: "transaction.write", label: "Write", tone: "write" },
					{ name: "transaction.read", label: "Read", tone: "read" },
				],
			},
			{
				label: "Transaction-sensitive",
				description: "Read sensitive transaction fields such as private codification data",
				permissions: [{ name: "transaction-sensitive.read", label: "Read", tone: "read" }],
			},
			{
				label: "Parcel (export)",
				description: "Allow downloading parcel resources such as shapefiles",
				permissions: [{ name: "parcel.export", label: "Export", tone: "action" }],
			},
		],
	},
	{
		title: "DEVELOPMENT POTENTIAL",
		rows: [
			{
				label: "Dev potential",
				description: "Read or update development potential data",
				permissions: [
					{ name: "devpotential.write", label: "Write", tone: "write" },
					{ name: "devpotential.read", label: "Read", tone: "read" },
				],
			},
		],
	},
	{
		title: "PROJECTS",
		rows: [
			{
				label: "Project",
				description: "Create and update projects",
				permissions: [{ name: "project.write", label: "Write", tone: "write" }],
			},
			{
				label: "Project (list)",
				description: "List the projects the user may access",
				permissions: [{ name: "project.list", label: "List", tone: "action" }],
			},
		],
	},
	{
		title: "CRM & BUYERS",
		rows: [
			{
				label: "CRM",
				description: "Read CRM data such as HubSpot deals, contacts, and contracts",
				permissions: [{ name: "crm.read", label: "Read", tone: "read" }],
			},
			{
				label: "Buyer",
				description: "Read buyers list and contact transaction data",
				permissions: [{ name: "buyer.read", label: "Read", tone: "read" }],
			},
		],
	},
	{
		title: "DOCUMENTS & AUTHORIZATION",
		rows: [
			{
				label: "Document store (upload)",
				description: "Upload files to document storage",
				permissions: [{ name: "documentstore.upload", label: "Upload", tone: "action" }],
			},
			{
				label: "Authorization",
				description: "Read or manage authorization metadata such as roles and permissions lists",
				permissions: [
					{ name: "authorization.write", label: "Manage", tone: "write" },
					{ name: "authorization.read", label: "Read", tone: "read" },
				],
			},
			{
				label: "User",
				description: "Create user accounts",
				permissions: [{ name: "user.create", label: "Create", tone: "action" }],
			},
		],
	},
	{
		title: "ANALYTICS",
		rows: [
			{
				label: "Dashboard",
				description: "Access the Landerz dashboard",
				permissions: [{ name: "dashboard.read", label: "Read", tone: "read" }],
			},
		],
	},
]

const router = useRouter()
const store = UseRootStore()

const loading = ref(true)
const error = ref("")
const roles = ref<RoleWithPermissions[]>([])

const categorizedPermissionNames = new Set(
	matrixSections.flatMap(section => section.rows.flatMap(row => row.permissions.map(permission => permission.name)))
)

const displayedSections = computed<MatrixSection[]>(() => {
	const uncategorizedRows = Array.from(assignedPermissionNames.value)
		.filter(permissionName => !categorizedPermissionNames.has(permissionName))
		.sort((a, b) => a.localeCompare(b))
		.map(permissionName => ({
			label: formatPermissionResource(permissionName),
			description: permissionName,
			permissions: [permissionToMatrixPermission(permissionName)],
		}))

	if (uncategorizedRows.length === 0) return matrixSections

	return [
		...matrixSections,
		{
			title: "OTHER PERMISSIONS",
			rows: uncategorizedRows,
		},
	]
})

const assignedPermissionNames = computed(() => {
	const names = new Set<string>()
	for (const role of roles.value) {
		for (const permission of role.permissions) {
			names.add(permission.name)
		}
	}
	return names
})

const roleHasPermission = (role: RoleWithPermissions, permissionName: string) =>
	role.permissions.some(permission => permission.name === permissionName)

const cellFor = (role: RoleWithPermissions, row: MatrixRow): MatrixPermission | null => {
	return row.permissions.find(permission => roleHasPermission(role, permission.name)) ?? null
}

const formatRoleName = (name: string) => {
	if (name.toLowerCase() === "cca") return "CCA"
	return name
		.split("_")
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ")
}

const formatPermissionResource = (permissionName: string) => {
	const [resource] = permissionName.split(".")
	return resource.replace(/[-_]/g, " ").replace(/\b\w/g, char => char.toUpperCase())
}

const permissionToMatrixPermission = (permissionName: string): MatrixPermission => {
	const action = permissionName.split(".").at(-1) ?? permissionName
	if (action === "read") return { name: permissionName, label: "Read", tone: "read" }
	if (action === "write") return { name: permissionName, label: "Write", tone: "write" }
	if (action === "create") return { name: permissionName, label: "Create", tone: "action" }
	if (action === "upload") return { name: permissionName, label: "Upload", tone: "action" }
	if (action === "export") return { name: permissionName, label: "Export", tone: "action" }
	if (action === "clear") return { name: permissionName, label: "Clear", tone: "action" }
	if (action === "move") return { name: permissionName, label: "Move", tone: "action" }
	if (action === "list") return { name: permissionName, label: "List", tone: "action" }

	return {
		name: permissionName,
		label: action.charAt(0).toUpperCase() + action.slice(1),
		tone: "action",
	}
}

const sortRoles = (allRoles: RoleWithPermissions[]) => {
	return [...allRoles].sort((a, b) => {
		const roleAIndex = roleOrder.indexOf(a.name)
		const roleBIndex = roleOrder.indexOf(b.name)
		if (roleAIndex === -1 && roleBIndex === -1) return a.name.localeCompare(b.name)
		if (roleAIndex === -1) return 1
		if (roleBIndex === -1) return -1
		return roleAIndex - roleBIndex
	})
}

onMounted(async () => {
	if (!store.currentUser?.has(Permission.AuthorizationRead) && !store.currentUser?.has(Permission.AuthorizationWrite)) {
		router.push("/")
		return
	}

	try {
		loading.value = true
		const allRoles = await getRolesWithPermissions(store.userApiToken)
		roles.value = sortRoles(allRoles.filter(role => role.name !== "debugger"))
	} catch (err) {
		error.value = err instanceof Error ? err.message : "Failed to load roles"
	} finally {
		loading.value = false
	}
})
</script>

<style scoped>
:deep(body),
:deep(html),
:deep(#app) {
	overflow-y: auto !important;
	height: auto !important;
	min-height: 100vh !important;
}

.permission-matrix-page {
	--permission-col-width: 260px;
	--description-col-width: 450px;
	--role-col-width: 138px;
	--main-head-height: 58px;
	--summary-head-height: 86px;

	max-width: 1600px;
	margin: 0 auto;
	height: 100vh;
	overflow-y: auto;
	padding: 2rem;
	position: relative;
}

.header {
	margin-bottom: 2rem;
}

.header-top {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 0.5rem;

	h1 {
		margin: 0 0 0.5rem 0;
		color: #333;
	}

	p {
		margin: 0 0 1rem 0;
		color: #666;
	}
}

.back-link {
	display: inline-block;
	color: #007bff;
	text-decoration: none;
	margin-top: 1rem;

	&:hover {
		text-decoration: underline;
	}
}

.loading,
.error {
	text-align: center;
	padding: 2rem;
	font-size: 1.1rem;
}

.error {
	color: #dc3545;
}

.matrix-content {
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	padding: 20px;
}

.matrix-section-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 20px;

	h2 {
		margin: 0;
		color: #333;
		font-size: 1.5rem;
	}
}

.matrix-scroll {
	max-height: 70vh;
	overflow: auto;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	background: white;
}

.permission-table {
	width: 100%;
	min-width: calc(var(--permission-col-width) + var(--description-col-width) + (7 * var(--role-col-width)));
	border-collapse: separate;
	border-spacing: 0;
	table-layout: fixed;
}

.permission-col {
	width: var(--permission-col-width);
}

.description-col {
	width: var(--description-col-width);
}

.role-col {
	width: var(--role-col-width);
}

th,
td {
	border-right: 1px solid #e9ecef;
	border-bottom: 1px solid #e9ecef;
}

.matrix-head-main th {
	position: sticky;
	top: 0;
	z-index: 10;
	height: var(--main-head-height);
	padding: 0 12px;
	background: #f8f9fa;
	color: #495057;
	font-size: 0.95rem;
	font-weight: 600;
	text-align: center;
	vertical-align: middle;
}

.matrix-head-description th {
	position: sticky;
	top: var(--main-head-height);
	z-index: 10;
	height: var(--summary-head-height);
	padding: 10px 12px;
	background: white;
	color: #616161;
	font-size: 0.78rem;
	font-style: normal;
	font-weight: 400;
	line-height: 1.35;
	text-align: center;
	vertical-align: middle;
}

.sticky-col {
	position: sticky;
	z-index: 6;
}

.permission-header,
.permission-summary,
.permission-cell {
	left: 0;
}

.description-header,
.description-summary,
.description-cell {
	left: var(--permission-col-width);
}

.permission-header,
.description-header {
	z-index: 14 !important;
}

.permission-summary,
.description-summary {
	z-index: 13 !important;
}

.role-header,
.role-summary {
	overflow-wrap: anywhere;

	&.permission-set {
		font-style: italic;
		background: var(--neutral-50);
	}
}

.section-row {
	height: 36px;

	.section-label {
		position: sticky;
		left: 0;
		z-index: 7;
		padding: 0 12px;
		background: #f8f9fa;
		color: #1976d2;
		font-size: 0.9rem;
		font-weight: 700;
		line-height: 36px;
		text-align: left;
	}

	td {
		background: #f8f9fa;
	}
}

.permission-row {
	height: 40px;
}

.permission-cell,
.description-cell {
	background: var(--neutral-0);
	text-align: left;
}

.permission-cell {
	padding: 12px;
	color: #333;
	font-size: 0.95rem;
	font-weight: 600;
	line-height: 1.35;
}

.description-cell {
	padding: 12px;
	color: #666;
	font-size: 0.85rem;
	font-style: normal;
	line-height: 1.35;
}

.access-cell {
	padding: 6px;
	background: var(--neutral-0);
	text-align: center;
	vertical-align: middle;
}

.access-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 68px;
	min-height: 28px;
	padding: 0 8px;
	border-radius: 6px;
	font-size: 0.9rem;
	font-weight: 600;
	line-height: 1;
}

.access-read {
	background: #e3f2fd;
	color: #0d47a1;
}

.access-write {
	background: #e8f5e9;
	color: #1b5e20;
}

.access-action {
	background: #fff3e0;
	color: #e65100;
}

.empty-access {
	color: #bdbdbd;
	font-size: 1rem;
	font-weight: 500;
}

@media (max-width: 760px) {
	.permission-matrix-page {
		--permission-col-width: 190px;
		--description-col-width: 280px;

		padding: 1rem;
	}

	.header-top {
		display: block;
	}
}
</style>
